/** Thin fetch wrapper: attaches the auth token and unwraps API errors. */

const TOKEN_KEY = "ccpoints.token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing — the session simply won't persist */
  }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }

  /** The server could not be reached at all, as opposed to refusing. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

/**
 * What a reader can act on. The app is served by the same process that serves
 * the API, so "the server is not running" is the overwhelmingly likely cause —
 * and on a laptop that usually means the terminal window was closed.
 */
export const OFFLINE_MESSAGE =
  "Can't reach the server. If you started it in a terminal, check that window is still open and the command still running.";

/**
 * fetch, but a dead server is a first-class answer rather than a raw
 * TypeError. Callers show ApiError.message, so this is what the reader sees.
 */
async function fetchOrOffline(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(0, OFFLINE_MESSAGE);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetchOrOffline(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeParse(text) : null;

  if (!res.ok) {
    const apiMessage =
      body && typeof body === "object" && "error" in body ? String((body as { error: unknown }).error) : "";
    const message = apiMessage || res.statusText || "Request failed";
    if (res.status === 401) setToken(null);
    throw new ApiError(res.status, message);
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Downloads a file from an authenticated endpoint. */
export async function download(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`/api${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, "Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
